import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum VerveCoffeeRequestForCoffeeLotItemKeys {
  idTag = 'idTag',
  name = 'name',
  weight = 'weight',
  price = 'price',
  amount = 'amount',
  fifteenPercentUnits = 'fifteenPercentUnits',
  fifteenPercentAmount = 'fifteenPercentAmount',
}

export const verveCoffeeRequestForCoffeeLotSchema = yup.object().shape({
  [VerveCoffeeRequestForCoffeeLotItemKeys.idTag]: yup.string().notRequired(),
  [VerveCoffeeRequestForCoffeeLotItemKeys.name]: yup.string().notRequired(),
  [VerveCoffeeRequestForCoffeeLotItemKeys.weight]: yup.string().notRequired(),
  [VerveCoffeeRequestForCoffeeLotItemKeys.price]: yup.string().notRequired(),
  [VerveCoffeeRequestForCoffeeLotItemKeys.amount]: yup.string().notRequired(),
  [VerveCoffeeRequestForCoffeeLotItemKeys.fifteenPercentUnits]: yup.string().notRequired(),
  [VerveCoffeeRequestForCoffeeLotItemKeys.fifteenPercentAmount]: yup.string().notRequired(),
})
export type VerveCoffeeRequestForCoffeeLot = yup.InferType<
  typeof verveCoffeeRequestForCoffeeLotSchema
>

export const verveCoffeeRequestForCoffeeAddressSchema = yup.object().shape({
  addressLine1: yup.string().notRequired(),
  addressLine2: yup.string().notRequired(),
  city: yup.string().notRequired(),
  state: yup.string().notRequired(),
  postalCode: yup.string().notRequired(),
  country: yup.string().notRequired(),
})
export type VerveCoffeeRequestForCoffeeAddress = yup.InferType<
  typeof verveCoffeeRequestForCoffeeAddressSchema
>

export const verveCoffeeRequestForCoffeeFileDataSchema = yup.object().shape({
  organizationLogoOverride: yup.string().notRequired(),
  date: yup.string().notRequired(),
  ref: yup.string().notRequired(),
  to: yup.string().notRequired(),
  terms: yup.string().notRequired(),
  vendorName: yup.string().notRequired(),
  vendorAddress: verveCoffeeRequestForCoffeeAddressSchema.required(),
  beneficiaryBankInformation: yup
    .object({
      bankName: yup.string().notRequired(),
      swift: yup.string().notRequired(),
      bankRoutingNumber: yup.string().notRequired(),
      address: yup.string().notRequired(),
    })
    .notRequired(),
  beneficiary: yup.string().notRequired(),
  intermediaryBankInformation: yup
    .object({
      bankName: yup.string().notRequired(),
      swift: yup.string().notRequired(),
      bankRoutingNumber: yup.string().notRequired(),
      address: yup.string().notRequired(),
    })
    .notRequired(),
  descriptionOfCoffee: yup.string().notRequired(),
  invoiceDetails: yup
    .object({
      invoiceNumber: yup.string().notRequired(),
      totalAmount: yup.string().notRequired(),
      paymentDueDate: yup.string().notRequired(),
    })
    .notRequired(),
  paymentAmount: yup.string().notRequired(),
  checkDepositAmount: yup.string().notRequired(),
  lastDayOfCropYear: yup.string().notRequired(),
  signature1: yup.string().notRequired(),
  signature2: yup.string().notRequired(),
  lots: yup.array().of(verveCoffeeRequestForCoffeeLotSchema).required(),
})

export type VerveCoffeeRequestForCoffeeFileData = yup.InferType<
  typeof verveCoffeeRequestForCoffeeFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<VerveCoffeeRequestForCoffeeFileData>

export const verveCoffeeRequestForCoffeeFileMappings: RequiredFileData = {
  organizationLogoOverride: '7c13f3e7-86d8-4518-a24d-9a5dfa760bfd',
  date: 'aa5cc2cc-f97f-448e-8925-35f193b6b262',
  ref: 'b22b31e5-68cc-4f72-83ca-70706087756a',
  to: '520bec8c-6d74-4925-aa88-5103ac712114',
  terms: '6f4dfb77-1842-424c-85d5-bf6e10a3d889',
  vendorName: 'e3377f8b-caa5-4074-9dad-a30a87c3ad2f',
  vendorAddress: {
    addressLine1: 'adc26b9c-074f-4b91-88bd-070fde080e48',
    addressLine2: '936a0867-dbbf-40df-b447-b149041ac32e',
    city: 'c4c6cb17-219f-46fa-ae6f-7a03fe70d6a0',
    state: 'adba0a83-3ea4-4d0c-81b9-ed5a96aeba29',
    postalCode: 'e75ed3f7-db1a-4e71-85bc-5f8a66c460ea',
    country: 'c9e0f2f7-1e8e-4ffe-aa12-be72a57bbf46',
  },
  beneficiaryBankInformation: {
    bankName: '62f6fdad-7beb-4c10-ac0e-f6f0b5b4e702',
    swift: '7e640501-4eed-497e-91b8-634a11dc68b2',
    bankRoutingNumber: '4f4b6be8-dbfd-4768-b0fc-e6335a2989dc',
    address: '042b6828-f9db-43c6-ba9f-e083687dcf8b',
  },
  beneficiary: 'cfad3f7e-ddc0-4919-ae4f-7ab2388ffc2e',
  intermediaryBankInformation: {
    bankName: '2d72161a-1726-481a-b8c2-7dca0b8f3296',
    swift: '40aafc0c-7033-4a43-927a-aabe639e54a1',
    bankRoutingNumber: '7990cc7c-e755-4e00-a90b-827844c9cb67',
    address: '0066377f-32e3-40a1-b33a-ca239fd683fa',
  },
  descriptionOfCoffee: 'd7bf84d9-b8c9-4dfa-b88e-d7d9d6711bf2',
  invoiceDetails: {
    invoiceNumber: '6820d056-a374-4d96-b411-0be14020b245',
    totalAmount: '3a4cd405-db93-4f4b-ad66-12fe68e22aaa',
    paymentDueDate: 'e567d868-8feb-4934-b3cd-110b0bca75b5',
  },
  paymentAmount: '7182d56f-32ee-451b-ad88-0543a87b8c8c',
  checkDepositAmount: '5c8bf382-6c8c-46e1-96b5-31c1aac9be48',
  lastDayOfCropYear: '625e3457-9e36-4e88-9424-547763b90368',
  signature1: 'b8881ffd-f4bf-4317-96b8-f64f13b123d0',
  signature2: '669ad0bc-3e14-4035-be9d-3bd170fe5d1c',
  lots: [
    {
      idTag: '64c01381-0e50-45ea-9268-d48adcf5d757',
      name: 'b0d1d16b-2261-40ba-ba3b-18ef5db20488',
      weight: 'a9dd9125-bde9-432a-86ba-8edc4dd1a5ec',
      price: 'edb53c75-535d-4e54-baa0-ec1d9da23f6c',
      amount: '03093f33-dd52-46c5-9056-08b0a55daf46',
      fifteenPercentUnits: '7ddcd08a-6223-490a-a819-7ab23f964844',
      fifteenPercentAmount: 'aaf3e57b-0995-4fe6-9895-bb40a2b3f4db',
    },
  ],
}
